import type { Theme } from "@mos-cat/ds-theme";
import type { BoxHTMLProps } from "reakit";
import { createComponent, createHook } from "reakit-system";
import type { ResponsiveValue } from "styled-system";
import type { BoxOptions } from "../Box/Box";
import { useBox } from "../Box/Box";
import type { StylesOptions } from "../hooks/useStyles";
import { useStyles } from "../hooks/useStyles";
import { useTheme } from "../hooks/useTheme";
import { cx, ecss } from "../reexports/emotion";
import { useRequiredPropWarning } from "../hooks/useRequiredPropWarning";

export type StackOptions = BoxOptions & {
  space: ResponsiveValue<keyof Theme["space"]>;
} & StylesOptions;

export type StackHTMLProps = BoxHTMLProps;

export type StackProps = StackOptions & StackHTMLProps;

export const useStack = createHook<StackOptions, BoxHTMLProps>({
  name: "Stack",
  keys: ["space"],
  compose: [useTheme, useBox, useStyles],
  useOptions(options): StackOptions {
    useRequiredPropWarning(Boolean(options.space), "Stack", "space");
    return {
      justifyContent: options.justifyContent ?? "flex-start",
      ...options,
      display: ["flex"],
      flexDirection: ["column"],
      styles: {
        ...(options.styles || {}),
        "& > * + *": {
          mt: options.space as ResponsiveValue<"mt", Theme>,
        },
      },
    };
  },
  useProps(options, props) {
    return {
      ...props,
      className: cx(props.className, ecss({ label: "Stack" })),
    };
  },
});

export const Stack = createComponent<"div", StackOptions>({
  useHook: useStack,
  as: "div",
  memo: true,
});
